import React, { useCallback, useEffect, useState } from 'react';
import lodash from 'lodash';
import Autosuggest from 'react-autosuggest';
import PropTypes from 'prop-types';
import AutoSuggestInputComponent from 'app/employer/components/autoSuggest/AutoSuggestInput';

const MIN_LEN_TO_FETCH_SUGGESTIONS = 3;
// Where the "apiPromise" is a function takes two arguments: strInput, queryParams and returns a promise
// handleApiDataResponse is a function that takes in the data response of the api and returns an array of suggestions

// After "apiPromise" has been resolved, the api response is passed to "handleDataApiDataResponse"
// REQUIREMENT is for the apiResponse/handleDataApiDataResponse is to return a list of object, where all objects must have a property
// called "label", which is used to render the suggestions and render the input after the user has selected a suggestion.

const autosuggestFactory = ({
   apiPromise,
   defaultPlaceholder = 'Search...',
   handleApiDataResponse = null,
   handleGetSuggestionValue = ({ label, name }) => {
      let suggestionValue = '';
      if (label !== undefined) {
         suggestionValue = label;
      } else if (name !== undefined) {
         suggestionValue = name;
      }
      return suggestionValue;
   },
   handleRenderSuggestion = (suggestion) => (handleGetSuggestionValue(suggestion)),
}) => {
   const fetchSuggestions = lodash.debounce(
      (
         strInput,
         queryParams,
         {
            filterSuggestions,
            setIsLoading,
            setSuggestions,
         },
      ) => {
         setIsLoading(true);
         return apiPromise({
            keyword: strInput,
            ...queryParams,
         }).then(({ data = [] }) => {
            const dataResponse = handleApiDataResponse != null
               ? handleApiDataResponse(data)
               : data;

            setIsLoading(false);
            if (lodash.isArray(dataResponse)) {
               const suggestions = filterSuggestions !== null
                  ? dataResponse.filter(filterSuggestions)
                  : dataResponse;

               setSuggestions(suggestions.slice(0, 10));
            }
         });
      },
      500, { trailing: true, leading: false, maxWait: 1000 },
   );

   // props.queryParameters = {} object
   const InnerAutosuggest = (props) => {
      const [suggestions, setSuggestions] = useState([]);
      const [isLoading, setIsLoading] = useState(false);

      useEffect(() => {
         return () => {
            fetchSuggestions.cancel();
         };
         // If fetchSuggestions is not in the dependencies,
         // console throws an error that there is a memory leak
      }, [fetchSuggestions]);

      const handleSuggestionsFetchRequested = useCallback(({ value: strInput }) => {
         if (lodash.isString(strInput) && strInput.length >= MIN_LEN_TO_FETCH_SUGGESTIONS) {
            fetchSuggestions(strInput,
               props.queryParams,
               {
                  filterSuggestions: props.filterSuggestions,
                  setIsLoading,
                  setSuggestions,
               });
         }
      }, [props.queryParams, props.filterSuggestions]);

      const handleSuggestionsClearRequested = useCallback(() => {
         setSuggestions([]);
      }, []);

      const handleChange = useCallback((event, { newValue }) => {
         props.handleChange({ name: newValue });
      }, []);

      const handleBlur = useCallback(() => {
         setIsLoading(false);
         props.handleBlur();
      }, []);

      const handleKeyDown = useCallback((event) => {
         if (event?.key === 'Enter') {
            event?.target?.blur();
            event?.preventDefault();
         }
      }, []);

      const handleSuggestionSelected = useCallback((event, { suggestion }) => {
         props.handleChange(suggestion);
      }, []);

      const value = lodash.isString(props.value) ? props.value : handleGetSuggestionValue(props.value, true);

      const inputProps = {
         value,
         isLoading,
         placeholder: props.placeholder,
         onChange: handleChange,
         onBlur: handleBlur,
         disabled: props.isDisabled,
         onKeyDown: handleKeyDown,
         className: props.className,
      };

      const handleRenderInputComponent = useCallback((argInputProps) => {
         return <AutoSuggestInputComponent inputProps={argInputProps}/>;
      }, []);

      return (
         <Autosuggest
            suggestions={suggestions}
            onSuggestionsFetchRequested={handleSuggestionsFetchRequested}
            onSuggestionsClearRequested={handleSuggestionsClearRequested}
            // When suggestion is clicked, Autosuggest needs to populate the input
            // based on the clicked suggestion. Teach Autosuggest how to calculate the
            // input value for every given suggestion.
            getSuggestionValue={handleGetSuggestionValue}
            // Use your imagination to render suggestions.
            renderSuggestion={handleRenderSuggestion}
            inputProps={inputProps}
            renderInputComponent={handleRenderInputComponent}
            onSuggestionSelected={handleSuggestionSelected}
         />
      );
   };

   InnerAutosuggest.propTypes = {
      handleChange: PropTypes.func.isRequired,
      handleBlur: PropTypes.func.isRequired,
      placeholder: PropTypes.string,
      isDisabled: PropTypes.bool,
      value: PropTypes.oneOfType([
         PropTypes.shape({}),
         PropTypes.string,
      ]),
      className: PropTypes.string,
      queryParams: PropTypes.shape({}),
      filterSuggestions: PropTypes.func,
   };

   InnerAutosuggest.defaultProps = {
      placeholder: defaultPlaceholder,
      isDisabled: false,
      value: '',
      className: '',
      queryParams: {},
      filterSuggestions: null,
   };

   return InnerAutosuggest;
};

export default autosuggestFactory;
